import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { auth, db, storage } from "./firebase";
import { outbox, type OutboxItem } from "./outbox";

const extensionForMime = (mimeType: string): string => {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
};

export const syncOutboxItem = async (
  item: OutboxItem,
): Promise<"queued" | "skipped"> => {
  const user = auth.currentUser;
  if (!user || !navigator.onLine) return "skipped";

  const jobRef = doc(collection(db, "jobs"), item.id);
  const existing = await getDoc(jobRef);
  if (existing.exists()) {
    await outbox.items.update(item.id, {
      status: "queued",
      updatedAt: Date.now(),
      errorMessage: undefined,
    });
    return "queued";
  }

  await outbox.items.update(item.id, {
    status: "uploading",
    attempts: item.attempts + 1,
    updatedAt: Date.now(),
    errorMessage: undefined,
  });

  try {
    let storagePath: string | undefined;

    if (item.inputKind === "audio") {
      if (!item.blob) throw new Error("The local recording is missing.");
      const mimeType = item.mimeType || item.blob.type || "audio/webm";
      storagePath = `users/${user.uid}/audio/${item.id}.${extensionForMime(mimeType)}`;
      const objectRef = ref(storage, storagePath);
      await uploadBytes(objectRef, item.blob, {
        contentType: mimeType,
        customMetadata: {
          clientId: item.id,
          jobKind: item.kind,
          deleteAfter: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
        },
      });

      // Force an authorization check now so the outbox does not report success
      // for an object the owner cannot later retrieve during a retry.
      await getDownloadURL(objectRef);
    }

    await setDoc(jobRef, {
      userId: user.uid,
      kind: item.kind,
      inputKind: item.inputKind,
      ...(storagePath ? { storagePath } : {}),
      ...(item.sourceText ? { sourceText: item.sourceText } : {}),
      ...(item.sourceUrl ? { sourceUrl: item.sourceUrl } : {}),
      clientId: item.id,
      status: "queued",
      createdAt: serverTimestamp(),
      device: navigator.userAgent.slice(0, 300),
      route: item.route,
    });

    await outbox.items.update(item.id, {
      status: "queued",
      updatedAt: Date.now(),
      errorMessage: undefined,
      blob: undefined,
    });
    return "queued";
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unable to send this item.";
    await outbox.items.update(item.id, {
      status: "failed",
      updatedAt: Date.now(),
      errorMessage,
    });
    throw error;
  }
};

export const syncReadyOutbox = async (): Promise<void> => {
  if (!auth.currentUser || !navigator.onLine) return;

  const items = await outbox.items
    .where("status")
    .anyOf(["ready", "failed"])
    .sortBy("createdAt");

  for (const item of items) {
    try {
      await syncOutboxItem(item);
    } catch {
      // Preserve the failed item and continue; one bad recording must not block
      // every later capture.
    }
  }
};

